import * as React from 'react'
import { AlertCircle as WarningCircleIcon, Cpu as ControllerChipIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  useGetPlantLineDetailQuery,
  useGetPlantLineControlCoverageQuery,
  useGetPlantLineOperationsQuery,
  useGetPlantTagsByScopeQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  orderByFeeds,
  type PlantComputeRef,
  type PlantEdge,
  type PlantControllerRef,
  type PlantLineControlNode,
  type PlantLineControlEquipment,
  type PlantUtilityRef
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantEquipmentIndexHref, PLANT_STRAY_SITE_SENTINEL } from '../../lib/plant-links'
import { computeIconFor } from './compute-icon'
import { equipmentIconFor } from './equipment-icon'
import { ProceduresSection } from './equipment-relationships'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { LaborStaffingSection } from './LaborStaffingSection'
import { LiveTagPanel } from './LiveTagPanel'
import { OperationWorkSections } from './OperationWorkSections'
import { PlantHref } from './PlantHref'
import {
  SessionBreadcrumbCurrent,
  SessionEntityMark,
  SessionGhostTile,
  SessionRollupPill,
  SessionTitleMark,
  countSessionMarks,
  sessionDetailHeaderClass,
  useVisibleGhosts
} from './session-mark-ui'
import { EquipmentTile } from './EquipmentTile'
import { EquipmentConnectsFlow } from './EquipmentConnectsFlow'
import { filterFlowEdges } from './flow-dag-layout'
import { SublineFlow } from './SublineFlow'

const GhostEquipmentIcon = equipmentIconFor(undefined)

/**
 * `/plant/equipment/sites/:siteId/lines/:lineId` — line OR subline detail.
 *
 * Both share the same shape (sublines are also `:Line` nodes) and the
 * same component. Renders a header with the instantiated process chip
 * (cross-tab to `/plant/processes?process=<id>`), the sublines flow when the
 * line has children, the directly-attached equipment grid, and a
 * sticky live-tag panel on the right.
 *
 * The breadcrumb walks Sites → site → (parent line) → this line so
 * subline pages always know how to navigate up by one level — the
 * parent line is resolved server-side via `<-[:HAS_SUBLINE]-`.
 */
const LineDetailPage = (): React.ReactElement => {
  const { siteId = '', lineId = '' } = useParams<{ siteId: string; lineId: string }>()
  const decodedSiteId = decodeRouteParam(siteId)
  const decodedLineId = decodeRouteParam(lineId)

  const { data, isLoading, isError } = useGetPlantLineDetailQuery(decodedLineId, {
    skip: !decodedLineId
  })
  const tagsQuery = useGetPlantTagsByScopeQuery(
    { kind: 'line', id: decodedLineId },
    { skip: !decodedLineId }
  )
  const controlQuery = useGetPlantLineControlCoverageQuery(decodedLineId, { skip: !decodedLineId })
  const laborQuery = useGetPlantLineOperationsQuery(decodedLineId, { skip: !decodedLineId })
  const controlNodes = React.useMemo(
    () => controlQuery.data?.lines ?? [],
    [controlQuery.data?.lines]
  )

  const line = data?.line
  const site = data?.site
  const isSubline = data?.parentLine !== null && data?.parentLine !== undefined
  const isStray =
    decodedSiteId === PLANT_STRAY_SITE_SENTINEL || (data !== undefined && site === null)
  const sublineOrder = React.useMemo(
    () => orderByFeeds(data?.sublines ?? [], data?.sublineFeeds ?? []),
    [data?.sublines, data?.sublineFeeds]
  )
  const sublines = sublineOrder.ordered
  const directEquipment = data?.directEquipment ?? []
  const equipmentConnectOrder = React.useMemo(
    () => orderByFeeds(directEquipment, data?.equipmentConnects ?? []),
    [directEquipment, data?.equipmentConnects]
  )
  const equipmentConnectEdges = React.useMemo(() => {
    const raw = (data?.equipmentConnects ?? []).map(toEquipmentConnectEdge)
    return filterFlowEdges(
      directEquipment.map((eq) => eq.id),
      raw
    )
  }, [data?.equipmentConnects, directEquipment])
  const additionalProperties = React.useMemo(
    () =>
      line ? collectAdditionalProperties('Line', line as unknown as Record<string, unknown>) : [],
    [line]
  )
  const { active, markFor, equipmentRollupByContainerId, equipmentDeletedRollupByContainerId } =
    usePlantSessionPaint()
  const lineMark = markFor(decodedLineId)
  const equipmentGhosts = useVisibleGhosts(decodedLineId, 'Equipment')
  const sublineGhosts = useVisibleGhosts(decodedLineId, 'Line')
  const equipmentAdded = React.useMemo(() => {
    if (!active) return 0
    const fromLive = countSessionMarks(
      directEquipment.map((eq) => eq.id),
      markFor,
      'added'
    )
    // New equipment may not be in the live list yet while paint is catching up.
    if (fromLive > 0 || directEquipment.length > 0) return fromLive
    return equipmentRollupByContainerId.get(decodedLineId) ?? 0
  }, [active, decodedLineId, directEquipment, equipmentRollupByContainerId, markFor])
  // Live equipment list — count marks directly (property updates omit containment).
  const equipmentModified = React.useMemo(() => {
    if (!active) return 0
    return countSessionMarks(
      directEquipment.map((eq) => eq.id),
      markFor,
      'touched'
    )
  }, [active, directEquipment, markFor])
  const equipmentDeleted = active
    ? (equipmentDeletedRollupByContainerId.get(decodedLineId) ?? 0)
    : 0

  return (
    <div className="relative grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 flex-col gap-card">
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
            Sites
          </Link>
          {isStray ? (
            <>
              <span className="mx-1.5 text-ink-4">/</span>
              <span className="text-ink-3">Strays</span>
            </>
          ) : (
            <>
              <span className="mx-1.5 text-ink-4">/</span>
              <PlantHref
                id={decodedSiteId}
                label="Site"
                node={site ?? undefined}
                className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
              >
                {site?.name ?? decodedSiteId}
                <SessionEntityMark entityId={decodedSiteId} />
              </PlantHref>
            </>
          )}
          {data?.parentLine && !isStray && (
            <>
              <span className="mx-1.5 text-ink-4">/</span>
              <PlantHref
                id={data.parentLine.id}
                label="Line"
                node={{ ...data.parentLine, siteId: decodedSiteId }}
                className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
              >
                {data.parentLine.name}
                <SessionEntityMark entityId={data.parentLine.id} />
              </PlantHref>
            </>
          )}
          <span className="mx-1.5 text-ink-4">/</span>
          <SessionBreadcrumbCurrent entityId={decodedLineId}>
            {line?.name ?? decodedLineId}
          </SessionBreadcrumbCurrent>
        </nav>

        {isError ? (
          <DetailFallback
            title="Couldn't load this line"
            hint="The line may have been deleted, or the proxy is unreachable."
          />
        ) : isLoading || !data || !line ? (
          <DetailFallback title="Loading line…" hint="" muted />
        ) : (
          <>
            <header className={sessionDetailHeaderClass('flex flex-col gap-2', lineMark)}>
              <div className="flex flex-wrap items-baseline gap-2">
                <h1 className="m-0 text-2xl font-semibold text-ink">{line.name}</h1>
                <SessionTitleMark mark={lineMark} />
                {isSubline && <span className="text-sm font-medium text-ink-3">· step</span>}
              </div>
              {line.description && (
                <p className="m-0 max-w-3xl text-sm text-ink-3">{line.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {data.process && (
                  <PlantHref
                    id={data.process.id}
                    label="Process"
                    node={data.process}
                    className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
                  >
                    <span aria-hidden>↳</span>
                    instantiates {data.process.name}
                    <SessionEntityMark entityId={data.process.id} />
                  </PlantHref>
                )}
                {data.controller && (
                  <PlantHref
                    id={data.controller.id}
                    label="Controller"
                    node={data.controller}
                    className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-0.5 text-11 font-medium text-violet-600 no-underline hover:bg-violet-500/20 dark:text-violet-300"
                    title={`Controlled by PLC ${data.controller.name}`}
                  >
                    <ControllerChipIcon size={11} aria-hidden />
                    {data.controller.name}
                    <SessionEntityMark entityId={data.controller.id} />
                  </PlantHref>
                )}
                {line.line_code && (
                  <span className="font-mono text-11 text-ink-3">{line.line_code}</span>
                )}
                {sublines.length > 0 && (
                  <Badge variant="outline">
                    {sublines.length} {sublines.length === 1 ? 'step' : 'steps'}
                  </Badge>
                )}
                <Badge variant="outline">
                  {directEquipment.length} {directEquipment.length === 1 ? 'piece' : 'pieces'} of
                  equipment
                </Badge>
                <SessionRollupPill
                  added={equipmentAdded}
                  modified={equipmentModified}
                  deleted={equipmentDeleted}
                />
                <PlantStrayPill isStray={data.isStray} />
              </div>
            </header>

            <ControlCoverageSection
              nodes={controlNodes}
              siteId={decodedSiteId}
              rootId={decodedLineId}
            />

            <ComputeSourcesSection computes={data.computes} />

            <LineUtilitiesSection utilities={data.utilities} />

            {(sublines.length > 0 || sublineGhosts.length > 0) && (
              <section>
                <SectionHeader
                  label={
                    sublineOrder.coverage === 'full' || sublineOrder.coverage === 'branching'
                      ? 'Process flow · Steps'
                      : 'Steps'
                  }
                  subtitle={`${sublines.length} ${sublines.length === 1 ? 'step' : 'steps'}`}
                />
                <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
                  {/* Inner scroller only — keep the card frame from becoming a
                      height-clipped overflow box when overflow-x is non-visible. */}
                  <div className="max-h-[min(28rem,70vh)] overflow-auto">
                    <SublineFlow
                      siteId={decodedSiteId}
                      sublines={sublines}
                      coverage={sublineOrder.coverage}
                      feeds={data.sublineFeeds ?? []}
                      ghosts={sublineGhosts}
                    />
                  </div>
                </div>
              </section>
            )}

            {directEquipment.length > 0 && equipmentConnectEdges.length > 0 && (
              <section>
                <SectionHeader
                  label={
                    equipmentConnectOrder.coverage === 'full' ||
                    equipmentConnectOrder.coverage === 'branching'
                      ? 'Equipment connectivity · CONNECTS_TO'
                      : 'Equipment connectivity'
                  }
                  subtitle={`${equipmentConnectEdges.length} ${
                    equipmentConnectEdges.length === 1 ? 'connection' : 'connections'
                  }`}
                />
                <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
                  <div className="max-h-[min(28rem,70vh)] overflow-auto">
                    <EquipmentConnectsFlow
                      equipment={equipmentConnectOrder.ordered}
                      coverage={equipmentConnectOrder.coverage}
                      connects={equipmentConnectEdges}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* LineOperation path (preferred) or legacy HumanLabor path. */}
            {laborQuery.isSuccess &&
              (laborQuery.data.mode === 'operations'
                ? laborQuery.data.operations.length > 0
                : laborQuery.data.legacy.stations.length > 0) && (
                <>
                  <OperationWorkSections
                    siteId={decodedSiteId}
                    labor={laborQuery.data}
                    directEquipment={directEquipment}
                  />
                  {equipmentGhosts.length > 0 && (
                    <section>
                      <SectionHeader
                        label="Deleted equipment"
                        subtitle={`${equipmentGhosts.length} this session`}
                      />
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))]">
                        {equipmentGhosts.map((ghost) => (
                          <SessionGhostTile
                            key={ghost.id}
                            name={ghost.name ?? ghost.id}
                            view={ghost.view}
                            icon={
                              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                                <GhostEquipmentIcon size={16} />
                              </span>
                            }
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}

            {laborQuery.isSuccess &&
              laborQuery.data.operations.length === 0 &&
              laborQuery.data.legacy.stations.length === 0 &&
              (directEquipment.length > 0 ||
                equipmentGhosts.length > 0 ||
                sublines.length === 0) && (
                <section>
                  <SectionHeader
                    label="Equipment"
                    subtitle={`${directEquipment.length} on this line`}
                  />
                  {directEquipment.length === 0 && equipmentGhosts.length === 0 ? (
                    <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
                      This line has no equipment yet.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))]">
                      {directEquipment.map((eq) => (
                        <EquipmentTile key={eq.id} equipment={eq} />
                      ))}
                      {equipmentGhosts.map((ghost) => (
                        <SessionGhostTile
                          key={ghost.id}
                          name={ghost.name ?? ghost.id}
                          view={ghost.view}
                          icon={
                            <span className="grid size-8 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                              <GhostEquipmentIcon size={16} />
                            </span>
                          }
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}

            {laborQuery.isSuccess && <LaborStaffingSection labor={laborQuery.data} />}

            <ProceduresSection procedures={data.procedures} />

            <EntityDetailsInspector entity={line} additionalProperties={additionalProperties} />
          </>
        )}
      </div>

      <LiveTagPanel
        title={`Live tags · ${line?.line_code ?? line?.name ?? 'line'}`}
        tags={tagsQuery.data?.tags ?? []}
        scopeEntityId={decodedLineId}
      />
    </div>
  )
}

/**
 * In-memory tree node built from the flat `PlantLineControlNode[]` roll-up.
 * `children` are sublines (`HAS_SUBLINE`); each node keeps its own direct
 * controllers and equipment-with-controllers.
 */
interface CoverageTreeNode extends PlantLineControlNode {
  children: CoverageTreeNode[]
}

/** Build the subline tree from the flat coverage list, rooted at `rootId`. */
function buildCoverageTree(nodes: PlantLineControlNode[], rootId: string): CoverageTreeNode | null {
  if (nodes.length === 0) return null
  const byId = new Map<string, CoverageTreeNode>()
  for (const n of nodes) byId.set(n.id, { ...n, children: [] })
  let root: CoverageTreeNode | null = null
  for (const node of byId.values()) {
    if (node.isRoot || node.id === rootId) root = root ?? node
    if (node.parentId) {
      const parent = byId.get(node.parentId)
      if (parent && parent.id !== node.id) parent.children.push(node)
    }
  }
  return root ?? byId.get(rootId) ?? null
}

/** A line/subline branch is worth rendering only if it (or a descendant) reaches a controller. */
function branchHasCoverage(node: CoverageTreeNode): boolean {
  if (node.controllers.length > 0) return true
  if (node.equipment.some((e) => e.controllers.length > 0)) return true
  return node.children.some(branchHasCoverage)
}

/**
 * Whether the coverage view is worth showing at all: a controller reached
 * through the line's *nested* structure (a subline, or any equipment). The
 * root line's own direct controller is intentionally excluded — that's
 * already surfaced as the header chip.
 */
function hasNestedCoverage(root: CoverageTreeNode): boolean {
  const equipmentControlled = (n: CoverageTreeNode): boolean =>
    n.equipment.some((e) => e.controllers.length > 0)
  if (equipmentControlled(root)) return true
  const walk = (n: CoverageTreeNode): boolean =>
    n.controllers.length > 0 || equipmentControlled(n) || n.children.some(walk)
  return root.children.some(walk)
}

function countCoverageControllers(root: CoverageTreeNode): number {
  const ids = new Set<string>()
  const walk = (n: CoverageTreeNode): void => {
    for (const c of n.controllers) ids.add(c.id)
    for (const e of n.equipment) for (const c of e.controllers) ids.add(c.id)
    n.children.forEach(walk)
  }
  walk(root)
  return ids.size
}

/**
 * Chips linking to the Compute host(s) serving a line's telemetry via
 * `MONITORED_BY`. Used inside the "Telemetry sources" section, which
 * rolls the line's own edge together with every Equipment in its subtree
 * (most `MONITORED_BY` edges sit at the equipment grain, so without the
 * roll-up a line page would surface no compute link at all).
 */
function ComputeChips({ computes }: { computes: PlantComputeRef[] }): React.ReactElement | null {
  if (!computes || computes.length === 0) return null
  return (
    <>
      {computes.map((compute) => {
        const Icon = computeIconFor(compute.compute_type)
        return (
          <PlantHref
            key={compute.id}
            id={compute.id}
            label="Compute"
            node={compute}
            className="inline-flex items-center gap-1 rounded-full border border-teal-400/40 bg-teal-500/10 px-2 py-0.5 text-11 font-medium text-teal-700 no-underline hover:bg-teal-500/20 dark:text-teal-300"
            title={`Monitored by ${compute.name}`}
          >
            <Icon size={11} aria-hidden />
            {compute.name}
          </PlantHref>
        )
      })}
    </>
  )
}

/** Utility service instances owned directly by this Line via HAS_UTILITY. */
function LineUtilitiesSection({
  utilities
}: {
  utilities: PlantUtilityRef[]
}): React.ReactElement | null {
  if (utilities.length === 0) return null

  return (
    <section>
      <SectionHeader
        label="Utilities"
        subtitle={`${utilities.length} ${utilities.length === 1 ? 'service' : 'services'} on this line`}
      />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        {utilities.map((utility) => (
          <div key={utility.id} className="flex min-w-0 flex-wrap items-center gap-2">
            <PlantHref
              id={utility.id}
              label="Utility"
              node={utility}
              className="text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
            >
              {utility.name}
            </PlantHref>
            {utility.utility_kind && (
              <Badge variant="outline" className="text-10">
                {utility.utility_kind.replaceAll('_', ' ')}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * "Telemetry sources" — the Compute hosts (OPC servers / devices /
 * gateways) serving this line's tags, rolled up across the line's own
 * `MONITORED_BY` edge and the edges of every Equipment in its subtree.
 * Each chip deep-links into the Compute tab; the inverse navigation lives
 * in the compute page's "Monitored equipment & lines" section.
 */
function ComputeSourcesSection({
  computes
}: {
  computes: PlantComputeRef[]
}): React.ReactElement | null {
  if (!computes || computes.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Telemetry sources"
        subtitle={`${computes.length} ${computes.length === 1 ? 'compute' : 'computes'} serving this line & its equipment`}
      />
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        <div className="flex flex-wrap gap-1.5">
          <ComputeChips computes={computes} />
        </div>
      </div>
    </section>
  )
}

function ControllerChips({
  controllers
}: {
  controllers: PlantControllerRef[]
}): React.ReactElement | null {
  if (controllers.length === 0) return null
  return (
    <span className="flex flex-wrap gap-1">
      {controllers.map((ctrl) => (
        <PlantHref
          key={ctrl.id}
          id={ctrl.id}
          label="Controller"
          node={ctrl}
          title={`PLC controller ${ctrl.name}`}
          className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-0.5 text-11 font-medium text-violet-600 no-underline hover:bg-violet-500/20 dark:text-violet-300"
        >
          <ControllerChipIcon size={10} aria-hidden />
          {ctrl.name}
        </PlantHref>
      ))}
    </span>
  )
}

function CoverageEquipmentRow({
  equipment
}: {
  equipment: PlantLineControlEquipment
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-0.5">
      <PlantHref
        id={equipment.id}
        label="Equipment"
        node={equipment}
        className="text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
      >
        {equipment.name}
      </PlantHref>
      {equipment.type && <span className="font-mono text-10 text-ink-4">{equipment.type}</span>}
      <ControllerChips controllers={equipment.controllers} />
    </div>
  )
}

function CoverageNode({
  node,
  siteId,
  isRoot
}: {
  node: CoverageTreeNode
  siteId: string
  isRoot: boolean
}): React.ReactElement {
  const equipmentWithCtrls = node.equipment.filter((e) => e.controllers.length > 0)
  const relevantChildren = node.children.filter(branchHasCoverage)
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {isRoot ? (
          <span className="text-13 font-semibold text-ink">{node.name}</span>
        ) : (
          <PlantHref
            id={node.id}
            label="Line"
            node={{ ...node, siteId }}
            className="text-13 font-semibold text-ink no-underline hover:text-brand hover:underline"
          >
            {node.name}
          </PlantHref>
        )}
        {!isRoot && <span className="text-10 uppercase tracking-[0.05em] text-ink-4">subline</span>}
        <ControllerChips controllers={node.controllers} />
      </div>
      {(equipmentWithCtrls.length > 0 || relevantChildren.length > 0) && (
        <div className="mt-1 flex flex-col gap-1 border-l border-border pl-3">
          {equipmentWithCtrls.map((eq) => (
            <CoverageEquipmentRow key={eq.id} equipment={eq} />
          ))}
          {relevantChildren.map((child) => (
            <CoverageNode key={child.id} node={child} siteId={siteId} isRoot={false} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * "Control coverage" — a nested roll-up of every PLC controller reachable
 * through this line's structure: the line's own controllers, its sublines
 * (recursively), and the equipment hung off any of them. Branches that
 * reach no controller are pruned. Hidden entirely when the only coverage
 * is the line's own direct controller (already shown as the header chip).
 */
function ControlCoverageSection({
  nodes,
  siteId,
  rootId
}: {
  nodes: PlantLineControlNode[]
  siteId: string
  rootId: string
}): React.ReactElement | null {
  const root = React.useMemo(() => buildCoverageTree(nodes, rootId), [nodes, rootId])
  if (!root || !hasNestedCoverage(root)) return null
  const count = countCoverageControllers(root)
  return (
    <section>
      <SectionHeader
        label="Control coverage"
        subtitle={`${count} PLC ${count === 1 ? 'controller' : 'controllers'} across sublines & equipment`}
      />
      <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        <CoverageNode node={root} siteId={siteId} isRoot />
      </div>
    </section>
  )
}

function toEquipmentConnectEdge(edge: PlantEdge): {
  source: string
  target: string
  regime?: string
} {
  const regime = edge.props?.regime
  return {
    source: edge.source,
    target: edge.target,
    ...(typeof regime === 'string' && regime.trim() ? { regime: regime.trim() } : {})
  }
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default LineDetailPage
